import type { PortalViewDto } from '@bqcrm/shared';
import { Alert, Box, Button, Card, Center, Container, Group, Loader, PinInput, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconLock, IconLogout } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useParams } from 'react-router';
import { api, errorText, HttpError } from '../api';
import { PortalView } from './portal/PortalView';

/** Wejście kodem z e-maila (urządzenie zapamiętane przez 30 dni). */
function CodeGate({ token, emailMasked, companyName, onIn }: { token: string; emailMasked: string; companyName: string; onIn: (v: PortalViewDto) => void }) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/public/strefa/${token}/kod`, { method: 'POST' });
      setSent(true);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const verify = async (value = code) => {
    setBusy(true);
    setError(null);
    try {
      onIn(await api<PortalViewDto>(`/public/strefa/${token}/wejscie`, { method: 'POST', body: { code: value } }));
    } catch (e) {
      setError(errorText(e));
      setCode('');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Container size={460} py={60}>
      <Card withBorder radius="md" padding="xl">
        <Stack>
          <Group gap="xs">
            <IconLock size={22} />
            <Title order={3}>Strefa klienta</Title>
          </Group>
          <Text size="sm">
            {companyName}: harmonogram prac, umowy i oferty w Twojej sprawie. Aby je zobaczyć, potwierdź adres e-mail jednorazowym kodem.
          </Text>
          {!sent ? (
            <Button onClick={() => void send()} loading={busy}>
              Wyślij kod na {emailMasked}
            </Button>
          ) : (
            <>
              <Text size="sm">
                Wysłaliśmy 6-cyfrowy kod na adres <b>{emailMasked}</b>. Kod jest ważny 15 minut.
              </Text>
              <Center>
                <PinInput
                  length={6}
                  type="number"
                  oneTimeCode
                  value={code}
                  onChange={setCode}
                  onComplete={(v) => void verify(v)}
                  disabled={busy}
                  aria-label="Kod z e-maila"
                />
              </Center>
              <Group justify="space-between">
                <Button variant="subtle" size="xs" onClick={() => void send()} disabled={busy}>
                  Wyślij nowy kod
                </Button>
                <Button onClick={() => void verify()} loading={busy} disabled={code.length !== 6}>
                  Wejdź
                </Button>
              </Group>
            </>
          )}
          {error && (
            <Alert color="red" icon={<IconAlertTriangle />}>
              {error}
            </Alert>
          )}
          <Text size="xs" c="dimmed">
            Po wpisaniu kodu to urządzenie zostanie zapamiętane na 30 dni. Na komputerze współdzielonym wyloguj się po zakończeniu.
          </Text>
        </Stack>
      </Card>
    </Container>
  );
}

/** Strefa klienta pod linkiem z e-maila (po podpisie umowy) — bez logowania, wejście kodem z e-maila. */
export function PublicPortalPage() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const key = ['public-portal', token];
  // pierwsze wejście liczy się jako wyświetlenie; potem odświeżanie co minutę i po powrocie do karty (bez liczenia)
  const loaded = useRef(false);
  const q = useQuery({
    queryKey: key,
    queryFn: async () => {
      const v = await api<PortalViewDto>(`/public/strefa/${token}${loaded.current ? '?odswiez=1' : ''}`);
      loaded.current = true;
      return v;
    },
    retry: false,
    refetchInterval: (query) => (query.state.data?.state === 'ok' && !query.state.error ? 60_000 : false),
    refetchOnWindowFocus: (query) => query.state.data?.state === 'ok' && !query.state.error,
  });
  const logout = async () => {
    await api(`/public/strefa/${token}/wyjscie`, { method: 'POST' }).catch(() => undefined);
    await qc.invalidateQueries({ queryKey: key });
  };

  if (q.isLoading)
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  // błąd sieci przy odświeżeniu nie zasłania danych; link unieważniony / sesja wygasła — komunikat
  if (q.error && (!q.data || (q.error instanceof HttpError && q.error.status < 500)))
    return (
      <Container size="sm" py={80}>
        <Alert color={q.error instanceof HttpError && q.error.status === 410 ? 'orange' : 'red'} title="Strefa klienta niedostępna" icon={<IconAlertTriangle />}>
          {errorText(q.error)}
        </Alert>
      </Container>
    );
  const v = q.data!;
  const demo = v.state === 'kod' ? v.demo : v.portal.demo;
  return (
    <Box bg="var(--app-subtle)" mih="100vh" pb="xl">
      {demo && (
        <Box bg="var(--app-tint-orange)" py={6}>
          <Text ta="center" size="sm" c="var(--app-text-orange-strong)">
            Środowisko demonstracyjne — dane i firmy są fikcyjne.
          </Text>
        </Box>
      )}
      {v.state === 'kod' ? (
        <CodeGate token={token} emailMasked={v.emailMasked} companyName={v.companyName} onIn={(nv) => {
            loaded.current = true;
            qc.setQueryData(key, nv);
          }} />
      ) : (
        <Container size="lg" pt="xl">
          <PortalView
            portal={v.portal}
            files={{
              offer: (id) => `/api/public/strefa/${token}/oferty/${id}/pdf`,
              contract: (id) => `/api/public/strefa/${token}/umowy/${id}/pdf`,
              signed: (id) => `/api/public/strefa/${token}/umowy/${id}/podpisana`,
              invoice: (id) => `/api/public/strefa/${token}/faktury/${id}/pdf`,
              protocol: (id) => `/api/public/strefa/${token}/protokoly/${id}/pdf`,
              protocolScan: (id) => `/api/public/strefa/${token}/protokoly/${id}/skan`,
              base: `/public/strefa/${token}`,
            }}
          />
          <Group justify="center" mt={48}>
            <Button variant="subtle" size="xs" color="gray" leftSection={<IconLogout size={14} />} onClick={() => void logout()}>
              Wyloguj to urządzenie
            </Button>
          </Group>
        </Container>
      )}
    </Box>
  );
}
